/** Backend endpointlari — bitta joyda (config/urls.py bilan mos). */
import { api, apiDownload, apiUpload, apiUploadWithProgress, BASE_URL } from "./client";
import type {
  AfforestationDashboard,
  AfforestationRow,
  AIReportResult,
  AppealCreated,
  AppealPlace,
  AppealListItem,
  AppealTrack,
  AuctionMeta,
  AuctionNotice,
  AuctionOrderDetail,
  AuctionOrderRow,
  AuctionPayment,
  AuctionReport,
  AuditEventItem,
  BaseMapRow,
  BiodiversityAssessmentRow,
  BiodiversityDashboard,
  CarbonBalanceRow,
  CarbonDashboard,
  CatalogRegion,
  ContractRow,
  DatasetInfo,
  DeviceHealth,
  DeviceRow,
  EmployeeRow,
  EmployeeStatistics,
  FeedbackStats,
  FeedbackSummary,
  FireDangerRow,
  GeoFeatureInfo,
  QrLinkStatus,
  QrLinkTicket,
  GeoFeatureRow,
  GeoJsonCollection,
  GeoportalObjects,
  GeoportalOptions,
  IntegrationHealth,
  IntegrationLogRow,
  IntegrationLogStatistics,
  IntegrationSystemRow,
  InventoryRow,
  InvoiceRow,
  AdminOverview,
  KpiSnapshot,
  IntegrationExchange,
  LeaseApplicationDetail,
  LeaseApplicationGeo,
  LeaseApplicationRow,
  LeaseDecisionVerification,
  LeaseDocumentRow,
  LeaseGisCheck,
  LeaseApplicationSummary,
  LeaseDashboard,
  LeaseMonitoringRow,
  LeaseMonitoringSummary,
  LeaseNoticeRow,
  LeaseOverlapResult,
  LeasePassport,
  LeasePlotRow,
  LeaseRegistrySummary,
  LeaseViolationRow,
  LandUsePlanProgress,
  LandUsePlanRow,
  LoginResponse,
  MapLayerRow,
  MeetingRow,
  MessageRow,
  MonitoringEvent,
  MovementRow,
  NotificationRow,
  OneIdAuthorize,
  OperatorDashboard,
  OrderIssueRow,
  OrderRow,
  OrderStatistics,
  OversightReport,
  Paginated,
  PaymentRow,
  PortalAbout,
  PortalAppealType,
  PortalFaqItem,
  PortalNewsItem,
  PortalNewsPage,
  SecurityKeyRow,
  PortalPage,
  PortalGuide,
  PortalMediaAsset,
  ProductRow,
  ProductStatistics,
  PublicHome,
  PublicMapConfig,
  PublicMapFilters,
  PublicStatistic,
  ExportReportQuery,
  ReportQuery,
  ReportResult,
  ReportTemplateRow,
  RiskScoreRow,
  ScenarioSimulationResult,
  ServiceContext,
  ServiceRedirect,
  ServiceRow,
  SessionRow,
  SnapshotRow,
  SpeciesRow,
  SpeciesStats,
  User,
  UserRow,
} from "./types";

/**
 * Ro'yxat so'rovlarining umumiy parametrlari.
 * Nomlar backenddagi `filterset_fields` bilan bir xil — qo'shimcha moslashtirish
 * kerak emas, bo'sh qiymatlar so'rovga qo'shilmaydi.
 */
/** ERI bilan tasdiqlangan qaror (backend `ApprovalPayloadSerializer` / `DecisionPayloadSerializer`). */
/** Ma'lumotnoma yozuvi (viloyat, tuman, yer toifasi…) — tanlash ro'yxati uchun. */
export interface CatalogOption {
  id: number;
  code: string;
  name: string;
}

export interface EriPayload {
  signature_ref: string;
  eri_serial?: string;
  comment?: string;
}

export interface DecisionPayload extends EriPayload {
  outcome: "approve" | "return" | "reject";
}

export type Query = Record<string, string | number | boolean | undefined | null>;

export function qs(params: Query = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export interface LoginPayload {
  email: string;
  password: string;
  otp?: string;
  trust_device?: boolean;
  /** Hududiy boshqarma kirish sahifasi («/hudud/kirish») uchun. */
  portal?: "regional";
}

export const authApi = {
  login: (payload: LoginPayload) => api.post<LoginResponse>("/auth/login/", payload),
  logout: () => api.post<{ detail: string }>("/auth/logout/"),
  me: (lang?: string) => api.get<User>("/auth/me/", { lang }),
  refresh: () => api.post<{ expires_in: number; csrf_token: string }>("/auth/refresh/"),
  /** Mobil ilovani QR orqali ulash: token yaratish, holatini kuzatish va bekor qilish. */
  qrCreate: () => api.post<QrLinkTicket>("/auth/qr/"),
  qrStatus: (ticket: string) => api.get<QrLinkStatus>(`/auth/qr/${encodeURIComponent(ticket)}/`),
  qrCancel: (ticket: string) => api.del<void>(`/auth/qr/${encodeURIComponent(ticket)}/`),
  oneidAuthorize: (next = "/kabinet") =>
    api.get<OneIdAuthorize>(`/auth/oneid/authorize/?next=${encodeURIComponent(next)}`),
  oneidCallback: (code: string, state: string) =>
    api.post<LoginResponse & { next: string; sandbox: boolean }>("/auth/oneid/callback/", {
      code,
      state,
    }),
  /** E-imzo (ERI kaliti) bilan kirish: holat, bir martalik matn va imzo bilan kirish. */
  eimzoStatus: () => api.get<{ configured: boolean; available: boolean }>("/auth/eimzo/status/"),
  eimzoChallenge: () =>
    api.post<{ challenge: string; expires_in: number }>("/auth/eimzo/challenge/"),
  eimzoLogin: (payload: { pkcs7: string; challenge: string }) =>
    api.post<LoginResponse & { created: boolean }>("/auth/eimzo/login/", payload),
  /** Smart Forestry USB tokeni (WebAuthn): kirish va tokenlarni boshqarish. */
  tokenLoginOptions: (email = "") =>
    api.post<Record<string, unknown>>("/auth/token/login/options/", { email }),
  tokenLogin: (payload: { state: string; credential: Record<string, unknown> }) =>
    api.post<LoginResponse>("/auth/token/login/", payload),
  tokenList: () => api.get<SecurityKeyRow[]>("/auth/token/"),
  tokenRegisterOptions: () => api.post<Record<string, unknown>>("/auth/token/register/options/"),
  tokenRegister: (payload: { credential: Record<string, unknown>; name: string }) =>
    api.post<SecurityKeyRow>("/auth/token/register/", payload),
  tokenDelete: (id: number) => api.del<void>(`/auth/token/${id}/`),
  /** Foydalanuvchilar reestri (ЧТЗ 4.1.6 — ma'muriyat bo'limi). */
  users: (params: Query = {}) => api.get<Paginated<UserRow>>(`/auth/users/${qs(params)}`),
  roles: () =>
    api.get<Array<{ id: number; code: string; name: string; permissions: string[] }>>(
      "/auth/roles/",
    ),
  sessions: () => api.get<SessionRow[] | Paginated<SessionRow>>("/auth/sessions/"),
  /** Sessiyani yakunlash — qurilma tizimdan chiqariladi. */
  revokeSession: (id: string) =>
    api.post<{ detail: string }>(`/auth/sessions/${encodeURIComponent(id)}/revoke/`),
  /** Tasdiqlash kodini tanlangan kanalga (SMS/E-mail) yuboradi. */
  mfaSendCode: (channel: "sms" | "email", target: string) =>
    api.post<{ detail: string }>("/auth/mfa/", { channel, target }),
  /** Kodni tasdiqlab MFA'ni yoqadi. */
  mfaConfirm: (channel: "sms" | "email", target: string, code: string) =>
    api.patch<{ detail: string }>("/auth/mfa/", { channel, target, code }),
  /** Joriy parol bilan tasdiqlab MFA'ni o'chiradi. */
  mfaDisable: (password: string) =>
    api.del<{ detail: string; requires_mfa: boolean }>("/auth/mfa/", { password }),
};

export const portalApi = {
  home: (lang?: string) => api.get<PublicHome>("/portal/public/", { lang }),
  status: () =>
    api.get<{ maintenance_mode: boolean; message: string; hotline: string; server_time: string }>(
      "/portal/public/status/",
    ),
  faq: (lang?: string) => api.get<PortalFaqItem[]>("/portal/public/faq/", { lang }),
  /** CMS sahifasi (`/pages/:slug`) — menyudagi «Ichki sahifa» bandlari. */
  page: (slug: string, lang?: string) =>
    api.get<PortalPage>(`/portal/public/pages/${encodeURIComponent(slug)}/`, { lang }),
  /** ЧТЗ 4.2.11.3 — murojaat formasi uchun hodisa turlari (avtorizatsiyasiz). */
  appealTypes: (lang?: string) =>
    api.get<PortalAppealType[]>("/portal/public/appeal-types/", { lang }),
  /** 1-blok — platforma haqida, rahbariyat, tuzilma, ochiq statistika. */
  about: (lang?: string) => api.get<PortalAbout>("/portal/public/about/", { lang }),
  statistics: (lang?: string) =>
    api.get<{ items: PublicStatistic[]; generated_at: string }>("/portal/public/statistics/", {
      lang,
    }),
  /** 3-blok — qo'llanmalar (jismoniy / yuridik shaxslar, PDF va video). */
  guides: (lang?: string) => api.get<PortalGuide[]>("/portal/public/guides/", { lang }),
  guideDownload: (id: number) =>
    api.post<{ url: string }>(`/portal/public/guides/${id}/download/`),
  /** 5-blok — multimedia galereya. */
  media: (lang?: string) => api.get<PortalMediaAsset[]>("/portal/public/media/", { lang }),
  /** 4.2.11.2 — publik xarita: qatlamlar, podkladkalar, filtrlar va obyektlar. */
  map: (lang?: string) => api.get<PublicMapConfig>("/portal/public/map/", { lang }),
  mapFilters: () => api.get<PublicMapFilters>("/portal/public/map/filters/"),
  /** Ochiq geoportal: ijara, ruxsatnoma, dorivor o'simliklar, o'rmon fondi va yashil hududlar. */
  geoportalObjects: (params: Query = {}, signal?: AbortSignal) =>
    api.get<GeoportalObjects>(`/portal/public/geoportal/objects/${qs(params)}`, { signal }),
  geoportalOptions: () => api.get<GeoportalOptions>("/portal/public/geoportal/options/"),
  mapFeatures: (layer: string, params: Query = {}, signal?: AbortSignal) =>
    api.get<GeoJsonCollection>(
      `/portal/public/map/layers/${encodeURIComponent(layer)}/${qs(params)}`,
      { signal },
    ),
  /** 4.2.11.5 — xizmat shlyuzi: kirgan bo'lsa xizmatga, aks holda OneID ga. */
  /**
   * `context` — xaritada tanlangan uchastka (raqam, maqsad, markaz). Backend uni
   * faqat integratsiyasi kelishilgan tashqi xizmatga uzatadi (`context: "transferred"`).
   */
  serviceRedirect: (code: string, next = "", context: ServiceContext = {}) =>
    api.get<ServiceRedirect>(
      `/portal/public/services/${encodeURIComponent(code)}/redirect/${qs({ next, ...context })}`,
    ),
  /** Barcha nashr etilgan yangiliklar (bosh sahifada faqat so'nggilari ko'rsatiladi). */
  news: (lang?: string) => api.get<PortalNewsItem[]>("/portal/public/news/", { lang }),
  /** To'liq ro'yxat: qidiruv (`q`), toifa, sana oralig'i va sahifalash. */
  newsPage: (
    params: { q?: string; category?: string; date_from?: string; date_to?: string; page: number; page_size?: number },
    lang: string,
    signal?: AbortSignal,
  ) =>
    api.get<PortalNewsPage>(`/portal/public/news/${qs({ ...params, lang })}`, { signal }),
  /** Maqola (to'liq matn, suratlar, manba, tadbir ma'lumotlari). */
  newsItem: (id: string, lang: string) =>
    api.get<PortalNewsItem>(`/portal/public/news/${encodeURIComponent(id)}/`, { lang }),
  /** 2.10 — baholash (avtorizatsiyasiz) va natijalar (tizimga kirganlar uchun). */
  feedback: (payload: { rating?: string; stars?: number; comment?: string; page?: string }) =>
    api.post<{ detail: string; duplicate: boolean }>("/portal/public/feedback/", payload),
  /** Bosh sahifadagi «Tizimni baholash» bloki: o'rtacha yulduz va baholar soni (ochiq). */
  feedbackSummary: () => api.get<FeedbackSummary>("/portal/public/feedback/summary/"),
  feedbackStats: (days = 30) => api.get<FeedbackStats>(`/portal/feedback/stats/${qs({ days })}`),
};

/**
 * Fuqaro murojaatlari — `apps.kuzatuv` (ЧТЗ 4.2.11.3 / 4.2.11.4).
 * Treking ID va telefonning oxirgi 4 raqami bilan yoki SMS-OTP orqali ishlaydi;
 * ikkalasi ham avtorizatsiyani talab qilmaydi, lekin so'rovlar cheklanadi.
 */
export const appealsApi = {
  /** ЧТЗ 4.2.11.3 — publik murojaat (multipart: foto/video bilan). */
  create: (form: FormData, onProgress: (ratio: number) => void = () => undefined) =>
    apiUploadWithProgress<AppealCreated>("/kuzatuv/appeals/public/", form, onProgress),
  /** Xaritadagi nuqta qaysi viloyat / tuman / o'rmon xo'jaligiga tushadi. */
  locate: (lat: number, lon: number) =>
    api.get<AppealPlace & { lat: number; lon: number; found: boolean }>(
      `/kuzatuv/appeals/public/locate/?lat=${lat.toFixed(6)}&lon=${lon.toFixed(6)}`,
    ),
  track: (payload: { appeal_id: string; phone_last4?: string; otp?: string }) =>
    api.post<AppealTrack>("/kuzatuv/appeals/track/", payload),
  trackOtp: (appealId: string) =>
    api.post<{ detail: string; phone_masked: string }>("/kuzatuv/appeals/track/otp/", {
      appeal_id: appealId,
    }),
};

export const reportsApi = {
  kpi: (period: string, range?: { from: string; to: string }) =>
    api.get<KpiSnapshot>(
      `/reports/kpi/?period=${encodeURIComponent(period)}${
        range ? `&date_from=${range.from}&date_to=${range.to}` : ""
      }`,
    ),
  adminOverview: () => api.get<AdminOverview>("/reports/admin-overview/"),
  /** Ad-hoc kesim: kunlik dinamika, hudud yoki SLA holati bo'yicha taqsimot. */
  run: (query: ReportQuery) => api.post<ReportResult>("/reports/run/", query),
  datasets: () => api.get<{ datasets: DatasetInfo[] }>("/reports/datasets/"),
  templates: (params: Query = {}) =>
    api.get<Paginated<ReportTemplateRow>>(`/reports/templates/${qs(params)}`),
  /**
   * Shablonni bajarish. Shablon URL'da ko'rsatilgan bo'lsa ham, `RunReportSerializer`
   * tanadan «template» yoki «dataset» ni talab qiladi — shuning uchun kod tanaga
   * ham qo'shiladi.
   */
  runTemplate: (code: string, body: Record<string, unknown> = {}) =>
    api.post<ReportResult>(`/reports/templates/${encodeURIComponent(code)}/run/`, {
      template: code,
      ...body,
    }),
  runs: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/reports/runs/${qs(params)}`),
  /** Faylni yuklab beradi (brauzer avtomatik saqlaydi) — sinxron eksport. */
  export: (query: ExportReportQuery) => apiDownload("/reports/export/", query),
  /** Murakkab tahlil + tabiiy tildagi xulosa (ЧТЗ, НОВОЕ). */
  aiReport: (query: ReportQuery) => api.post<AIReportResult>("/reports/ai-report/", query),
  /** Xavf ballari va senariy modellashtirish (ЧТЗ, НОВОЕ — qaror qabul qilishni qo'llab-quvvatlash). */
  riskScores: (params: Query = {}) =>
    api.get<Paginated<RiskScoreRow>>(`/reports/risk-scores/${qs(params)}`),
  simulateScenario: (district: string, adjustments: Record<string, number>) =>
    api.post<ScenarioSimulationResult>("/reports/scenarios/simulate/", { district, adjustments }),
};

/** Kuzatuv — operator paneli, murojaatlar va bildirishnomalar (ЧТЗ 4.2.8). */
export const kuzatuvApi = {
  dashboard: () => api.get<OperatorDashboard>("/kuzatuv/dashboard/"),
  appeals: (params: Query = {}) => {
    const query = { page_size: 8, ordering: "-created_at", ...params };
    return api.get<Paginated<AppealListItem>>(`/kuzatuv/appeals/${qs(query)}`);
  },
  appeal: (id: string) => api.get<Record<string, unknown>>(`/kuzatuv/appeals/${id}/`),
  slaDashboard: () => api.get<Record<string, unknown>>("/kuzatuv/appeals/sla-dashboard/"),
  notifications: (params: Query = {}) =>
    api.get<Paginated<NotificationRow>>(`/kuzatuv/notifications/${qs(params)}`),
  unreadCount: () => api.get<{ unread: number }>("/kuzatuv/notifications/unread-count/"),
  markAllRead: () => api.post<{ updated: number }>("/kuzatuv/notifications/mark-all-read/"),
};

/** Monitoring — hodisalar, IoT qurilmalar va yong'in xavfi (ЧТЗ 4.2.2). */
export const monitoringApi = {
  events: (params: Query = {}) =>
    api.get<Paginated<MonitoringEvent>>(
      `/monitoring/events/${qs({ ordering: "-detected_at", ...params })}`,
    ),
  event: (id: string) => api.get<Record<string, unknown>>(`/monitoring/events/${id}/`),
  eventsGeojson: (params: Query = {}) =>
    api.get<GeoJsonCollection>(`/monitoring/events/geojson/${qs(params)}`),
  eventsDashboard: () => api.get<Record<string, unknown>>("/monitoring/events/dashboard/"),
  devices: (params: Query = {}) => api.get<Paginated<DeviceRow>>(`/monitoring/devices/${qs(params)}`),
  deviceHealth: () => api.get<DeviceHealth>("/monitoring/devices/health/"),
  snapshots: (params: Query = {}) =>
    api.get<Paginated<SnapshotRow>>(`/monitoring/snapshots/${qs(params)}`),
  fireDanger: (params: Query = {}) =>
    api.get<Paginated<FireDangerRow>>(`/monitoring/fire-danger/${qs({ ordering: "-date", ...params })}`),
  /** Sensor o'lchovlari kursor bo'yicha sahifalanadi — qurilma majburiy. */
  readings: (device: number, params: Query = {}) =>
    api.get<{ results: Array<Record<string, unknown>>; next: string | null }>(
      `/monitoring/readings/${qs({ device, ...params })}`,
    ),
};

/** Geoportal — qatlamlar, geoobyektlar va podkladkalar (ЧТЗ 4.2.1). */
export const gisApi = {
  layers: (params: Query = {}) => api.get<MapLayerRow[]>(`/gis/layers/${qs(params)}`),
  basemaps: () => api.get<BaseMapRow[]>("/gis/basemaps/"),
  features: (params: Query = {}) => api.get<Paginated<GeoFeatureRow>>(`/gis/features/${qs(params)}`),
  /** Kichik qatlam GeoJSON'i (`limit` + `offset`); katta qatlamlar xaritaga vektor plitkalar bilan beriladi. */
  featuresGeojson: (params: Query = {}) =>
    api.get<GeoJsonCollection>(`/gis/features/geojson/${qs(params)}`),
  /** Xarita popup'i uchun obyekt atributlari (geometriyasiz). */
  featureInfo: (id: string) => api.get<GeoFeatureInfo>(`/gis/features/${id}/info/`),
  moderation: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/gis/moderation/${qs(params)}`),
  /** Bioxilma-xillik baholash (ЧТЗ, НОВОЕ — НСМЛ). */
  biodiversityAssessments: (params: Query = {}) =>
    api.get<Paginated<BiodiversityAssessmentRow>>(`/gis/biodiversity-assessments/${qs(params)}`),
  biodiversityAssessment: (id: string) =>
    api.get<Record<string, unknown>>(`/gis/biodiversity-assessments/${id}/`),
  biodiversityDashboard: () =>
    api.get<BiodiversityDashboard>("/gis/biodiversity-assessments/dashboard/"),
  biodiversityGeojson: (params: Query = {}) =>
    api.get<GeoJsonCollection>(`/gis/biodiversity-assessments/geojson/${qs(params)}`),
  /** Uglerod balansi (ЧТЗ, НОВОЕ — НСМЛ). */
  carbonBalance: (params: Query = {}) =>
    api.get<Paginated<CarbonBalanceRow>>(`/gis/carbon-balance/${qs(params)}`),
  carbonDashboard: (params: Query = {}) =>
    api.get<CarbonDashboard>(`/gis/carbon-balance/dashboard/${qs(params)}`),
};

/** O'rmon tiklash — Yashil makon obyektlari (ЧТЗ 4.2.5). */
export const afforestationApi = {
  objects: (params: Query = {}) =>
    api.get<Paginated<AfforestationRow>>(`/afforestation/objects/${qs(params)}`),
  object: (id: string) => api.get<Record<string, unknown>>(`/afforestation/objects/${id}/`),
  dashboard: () => api.get<AfforestationDashboard>("/afforestation/objects/dashboard/"),
  /** Xarita qatlami. Katta hudud `limit` + `offset` bilan bo'lib olinadi. */
  geojson: (params: Query = {}, options: { onProgress?: (ratio: number) => void } = {}) =>
    api.get<GeoJsonCollection>(`/afforestation/objects/geojson/${qs(params)}`, options),
  acts: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/afforestation/acts/${qs(params)}`),
  /** Kompleks yer foydalanish rejalari (ЧТЗ, НОВОЕ — ПП-140 «ВЛЛ»). */
  plans: (params: Query = {}) =>
    api.get<Paginated<LandUsePlanRow>>(`/afforestation/plans/${qs(params)}`),
  plan: (id: string) => api.get<Record<string, unknown>>(`/afforestation/plans/${id}/`),
  createPlan: (payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>("/afforestation/plans/", payload),
  planProgress: (id: string) => api.get<LandUsePlanProgress>(`/afforestation/plans/${id}/progress/`),
  submitPlan: (id: string) => api.post<Record<string, unknown>>(`/afforestation/plans/${id}/submit/`),
  approvePlan: (id: string) => api.post<Record<string, unknown>>(`/afforestation/plans/${id}/approve/`),
  activatePlan: (id: string) => api.post<Record<string, unknown>>(`/afforestation/plans/${id}/activate/`),
  archivePlan: (id: string, reason = "") =>
    api.post<Record<string, unknown>>(`/afforestation/plans/${id}/archive/`, { reason }),
  plansGeojson: (params: Query = {}) =>
    api.get<GeoJsonCollection>(`/afforestation/plans/geojson/${qs(params)}`),
};

/** Ijara uchastkalari (ЧТЗ 4.2.4). */
export const leaseApi = {
  plots: (params: Query = {}) => api.get<Paginated<LeasePlotRow>>(`/lease/plots/${qs(params)}`),
  plot: (id: string) => api.get<Record<string, unknown>>(`/lease/plots/${id}/`),
  dashboard: () => api.get<LeaseDashboard>("/lease/plots/dashboard/"),
  /** GIS reestr jamlanmasi — holatlar, muddatlar, moliyaviy yakunlar. */
  registry: (params: Query = {}) =>
    api.get<LeaseRegistrySummary>(`/lease/plots/registry/${qs(params)}`),
  /** Xarita qatlami. Katta hudud `limit` + `offset` bilan bo'lib olinadi. */
  geojson: (params: Query = {}, options: { onProgress?: (ratio: number) => void } = {}) =>
    api.get<GeoJsonCollection>(`/lease/plots/geojson/${qs(params)}`, options),
  /** Uchastkaning elektron pasporti (shartnoma, to'lov, monitoring, hujjatlar). */
  passport: (id: string) => api.get<LeasePassport>(`/lease/plots/${id}/passport/`),
  plotNotices: (id: string) => api.get<LeaseNoticeRow[]>(`/lease/plots/${id}/notices/`),
  planMonitoring: (id: string, payload: { method?: string; planned_date?: string | null }) =>
    api.post<LeaseMonitoringRow>(`/lease/plots/${id}/plan-monitoring/`, payload),
  terminate: (id: string, reason: string) =>
    api.post<Record<string, unknown>>(`/lease/plots/${id}/terminate/`, { reason }),
  runReminders: () =>
    api.post<{ payment_notices: number; contract_notices: number }>(
      "/lease/plots/run-reminders/",
    ),
  auctions: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/lease/auction-records/${qs(params)}`),

  /* --- arizalar va E-IJARA jarayoni --- */
  applications: (params: Query = {}) =>
    api.get<Paginated<LeaseApplicationRow>>(`/lease/applications/${qs(params)}`),
  application: (id: string) => api.get<LeaseApplicationDetail>(`/lease/applications/${id}/`),
  applicationSummary: () =>
    api.get<LeaseApplicationSummary>("/lease/applications/summary/"),
  /** Holat ranglari bilan xarita qatlami (GIS). */
  applicationsGeojson: (params: Query = {}) =>
    api.get<LeaseApplicationGeo>(`/lease/applications/geojson/${qs(params)}`),
  createApplication: (payload: Record<string, unknown>) =>
    api.post<LeaseApplicationDetail>("/lease/applications/", payload),
  updateApplication: (id: string, payload: Record<string, unknown>) =>
    api.patch<LeaseApplicationDetail>(`/lease/applications/${id}/`, payload),
  /** Xaritada chizilgan poligonni ariza ochmasdan tekshirish. */
  checkOverlap: (geometry: unknown, excludePlot?: string) =>
    api.post<LeaseOverlapResult>("/lease/applications/check-overlap/", {
      geometry,
      exclude_plot: excludePlot ?? null,
    }),
  gisCheck: (id: string) =>
    api.post<{ ok: boolean; application: LeaseApplicationDetail }>(
      `/lease/applications/${id}/gis-check/`,
    ),
  gisChecks: (id: string) =>
    api.get<LeaseGisCheck[]>(`/lease/applications/${id}/gis-checks/`),
  /** O'rmon xo'jaligi: ariza tasdig'i (ERI) — E-IJARAga avtomatik yuboriladi. */
  forestryApprove: (id: string, payload: EriPayload) =>
    api.post<LeaseApplicationDetail>(`/lease/applications/${id}/forestry-approve/`, payload),
  /** Agentlik: nol stavka bo'yicha dastlabki qaror. */
  agencyZeroRate: (id: string, payload: DecisionPayload) =>
    api.post<LeaseApplicationDetail>(`/lease/applications/${id}/agency-zero-rate/`, payload),
  /** O'rmon xo'jaligi: tayyor loyihani kelishish / qaytarish. */
  forestryProject: (id: string, payload: DecisionPayload) =>
    api.post<LeaseApplicationDetail>(`/lease/applications/${id}/forestry-project/`, payload),
  /** Agentlik: yakuniy kelishuv. */
  agencyFinal: (id: string, payload: DecisionPayload) =>
    api.post<LeaseApplicationDetail>(`/lease/applications/${id}/agency-final/`, payload),
  cancelApplication: (id: string, reason: string) =>
    api.post<LeaseApplicationDetail>(`/lease/applications/${id}/cancel/`, { reason }),
  terminateApplication: (id: string, reason: string) =>
    api.post<LeaseApplicationDetail>(`/lease/applications/${id}/terminate/`, { reason }),
  retryExchange: (id: string, exchangeId: string) =>
    api.post<IntegrationExchange>(`/lease/applications/${id}/exchanges/${exchangeId}/retry/`),
  uploadDocument: (id: string, form: FormData) =>
    apiUpload<LeaseDocumentRow>(`/lease/applications/${id}/upload/`, form, "POST"),
  documentsHistory: (id: string) =>
    api.get<LeaseDocumentRow[]>(`/lease/applications/${id}/documents-history/`),
  /** Ariza blankasi (Word / PDF) — brauzerda yuklab olinadi yoki ochiladi. */
  documentUrl: (id: string, format: "docx" | "pdf", options: { inline?: boolean } = {}) =>
    `${BASE_URL}/lease/applications/${id}/document/${qs({ format, inline: options.inline ? 1 : undefined })}`,
  /** Qaror QR-kodi ochiladigan ochiq tekshiruv (login talab qilinmaydi). */
  verifyDecision: (id: string) => api.get<LeaseDecisionVerification>(`/lease/verify/${id}/`),

  /* --- monitoring va nazorat (TZ 13-15-bo'limlar) --- */
  monitorings: (params: Query = {}) =>
    api.get<Paginated<LeaseMonitoringRow>>(`/lease/monitorings/${qs(params)}`),
  monitoringSummary: () => api.get<LeaseMonitoringSummary>("/lease/monitorings/summary/"),
  startMonitoring: (id: string, payload: { lat?: number; lon?: number } = {}) =>
    api.post<LeaseMonitoringRow>(`/lease/monitorings/${id}/start/`, payload),
  completeMonitoring: (id: string, payload: Record<string, unknown>) =>
    api.post<LeaseMonitoringRow>(`/lease/monitorings/${id}/complete/`, payload),

  violations: (params: Query = {}) =>
    api.get<Paginated<LeaseViolationRow>>(`/lease/violations/${qs(params)}`),
  createViolation: (payload: Record<string, unknown>) =>
    api.post<LeaseViolationRow>("/lease/violations/", payload),
  warnViolation: (id: string) => api.post<LeaseViolationRow>(`/lease/violations/${id}/warn/`),
  recheckViolation: (id: string, plannedDate?: string) =>
    api.post<LeaseViolationRow>(`/lease/violations/${id}/recheck/`, {
      planned_date: plannedDate ?? null,
    }),
  resolveViolation: (id: string, note: string) =>
    api.post<LeaseViolationRow>(`/lease/violations/${id}/resolve/`, { note }),
  escalateViolation: (id: string, authority: string, note = "") =>
    api.post<LeaseViolationRow>(`/lease/violations/${id}/escalate/`, { authority, note }),

  notices: (params: Query = {}) =>
    api.get<Paginated<LeaseNoticeRow>>(`/lease/notices/${qs(params)}`),
};

/** E-AUKSION: buyurtma, xabarnoma, bayonnoma, to'lov va taqsimot (agentlik ko'rinishi). */
export const eauksionApi = {
  meta: () => api.get<AuctionMeta>("/lease/eauksion/meta/"),
  orders: (params: Query = {}) =>
    api.get<Paginated<AuctionOrderRow>>(`/lease/eauksion/orders/${qs(params)}`),
  order: (id: string) => api.get<AuctionOrderDetail>(`/lease/eauksion/orders/${id}/`),
  ordersExportUrl: (params: Query = {}) => `${BASE_URL}/lease/eauksion/orders/export/${qs(params)}`,
  notices: (params: Query = {}) =>
    api.get<Paginated<AuctionNotice>>(`/lease/eauksion/notices/${qs(params)}`),
  noticeCounts: () =>
    api.get<{ total: number; unread: number; read: number }>("/lease/eauksion/notices/counts/"),
  markRead: (id: number) => api.post<AuctionNotice>(`/lease/eauksion/notices/${id}/mark-read/`),
  markAllRead: () => api.post<{ updated: number }>("/lease/eauksion/notices/mark-all-read/"),
  payments: (params: Query = {}) =>
    api.get<Paginated<AuctionPayment>>(`/lease/eauksion/payments/${qs(params)}`),
  report: (params: Query = {}) => api.get<AuctionReport>(`/lease/eauksion/report/${qs(params)}`),
  reportExportUrl: (params: Query = {}) =>
    `${BASE_URL}/lease/eauksion/report/${qs({ ...params, format: "xlsx" })}`,
  /** «E-AUKSION ga o'tish» — buyurtmachi kabinetiga havola (o'tish audit qilinadi). */
  handoff: () => api.post<{ url: string; service: string; sso: boolean }>("/lease/eauksion/handoff/"),
};

/** Billing — shartnoma, invoys, to'lov (ЧТЗ 4.2.9). */
export const billingApi = {
  contracts: (params: Query = {}) =>
    api.get<Paginated<ContractRow>>(`/billing/contracts/${qs(params)}`),
  invoices: (params: Query = {}) => api.get<Paginated<InvoiceRow>>(`/billing/invoices/${qs(params)}`),
  payments: (params: Query = {}) => api.get<Paginated<PaymentRow>>(`/billing/payments/${qs(params)}`),
  transactions: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/billing/transactions/${qs(params)}`),
  tariffs: () => api.get<Array<Record<string, unknown>>>("/billing/tariffs/"),
};

/** Mahsulotlar va ombor (ЧТЗ 4.2.6). */
export const productsApi = {
  products: (params: Query = {}) =>
    api.get<Paginated<ProductRow>>(`/products/products/${qs(params)}`),
  statistics: () => api.get<ProductStatistics>("/products/products/statistics/"),
  movements: (params: Query = {}) =>
    api.get<Paginated<MovementRow>>(`/products/movements/${qs(params)}`),
  inventories: (params: Query = {}) =>
    api.get<Paginated<InventoryRow>>(`/products/inventories/${qs(params)}`),
  stands: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/products/stands/${qs(params)}`),
  stockSummary: () => api.get<Record<string, unknown>>("/products/stands/stock-summary/"),
};

/** «Yashil olam» bozori (ЧТЗ 4.2.7). */
export const marketApi = {
  orders: (params: Query = {}) => api.get<Paginated<OrderRow>>(`/market/orders/${qs(params)}`),
  statistics: () => api.get<OrderStatistics>("/market/orders/statistics/"),
  services: (params: Query = {}) => api.get<ServiceRow[]>(`/market/services/${qs(params)}`),
  issues: (params: Query = {}) =>
    api.get<Paginated<OrderIssueRow>>(`/market/issues/${qs(params)}`),
  resolveIssue: (id: string, resolution: string) =>
    api.post<OrderIssueRow>(`/market/issues/${id}/resolve/`, { resolution }),
  catalog: (params: Query = {}) =>
    api.get<Paginated<ProductRow>>(`/market/catalog/${qs(params)}`),
};

/** Korporativ kabinet — xodimlar, yig'ilishlar, ichki xatlar (ЧТЗ 4.2.10). */
export const corporateApi = {
  employees: (params: Query = {}) =>
    api.get<Paginated<EmployeeRow>>(`/corporate/employees/${qs(params)}`),
  employeeStatistics: () => api.get<EmployeeStatistics>("/corporate/employees/statistics/"),
  meetings: (params: Query = {}) => api.get<Paginated<MeetingRow>>(`/corporate/meetings/${qs(params)}`),
  messages: (params: Query = {}) => api.get<Paginated<MessageRow>>(`/corporate/messages/${qs(params)}`),
  directory: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/corporate/directory/${qs(params)}`),
};

/** Tashqi tizimlar holati — «Integratsiyalar» bo'limi (ЧТЗ 4.1.2). */
export const integrationsApi = {
  health: () => api.get<IntegrationHealth>("/integrations/health/"),
  systems: (params: Query = {}) =>
    api.get<Paginated<IntegrationSystemRow>>(`/integrations/systems/${qs(params)}`),
  logs: (params: Query = {}) =>
    api.get<Paginated<IntegrationLogRow>>(`/integrations/logs/${qs(params)}`),
  logStatistics: (params: Query = {}) =>
    api.get<IntegrationLogStatistics>(`/integrations/logs/statistics/${qs(params)}`),
  outbox: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/integrations/outbox/${qs(params)}`),
  outboxSummary: () => api.get<Record<string, unknown>>("/integrations/outbox/summary/"),
  healthcheck: (code: string) =>
    api.post<Record<string, unknown>>(`/integrations/systems/${encodeURIComponent(code)}/healthcheck/`),
};

/** Ma'lumotnomalar — hudud kodi/id'sini nomga aylantirish uchun. */
export const catalogApi = {
  regions: async (lang?: string) => {
    const data = await api.get<Paginated<CatalogRegion> | CatalogRegion[]>(
      "/catalog/regions/?page_size=100",
      { lang },
    );
    // Ba'zi ma'lumotnoma viewsetlarida sahifalash o'chirilgan — ikkala shakl ham
    // qo'llab-quvvatlanadi.
    return Array.isArray(data) ? data : data.results;
  },
  forestries: async (lang?: string) => {
    const data = await api.get<
      Paginated<{ id: number; code: string; name: string }> | Array<{ id: number; code: string; name: string }>
    >("/catalog/forestries/?page_size=200", { lang });
    return Array.isArray(data) ? data : data.results;
  },
  /** Ijara arizasi formasi uchun ma'lumotnomalar (sahifalash o'chirilgan bo'lishi mumkin). */
  districts: async (params: Query = {}) => {
    const data = await api.get<Paginated<CatalogOption> | CatalogOption[]>(
      `/catalog/districts/${qs({ page_size: 300, ...params })}`,
    );
    return Array.isArray(data) ? data : data.results;
  },
  forestriesBy: async (params: Query = {}) => {
    const data = await api.get<Paginated<CatalogOption> | CatalogOption[]>(
      `/catalog/forestries/${qs({ page_size: 300, ...params })}`,
    );
    return Array.isArray(data) ? data : data.results;
  },
  landCategories: async () => {
    const data = await api.get<Paginated<CatalogOption> | CatalogOption[]>(
      "/catalog/land-categories/?page_size=100",
    );
    return Array.isArray(data) ? data : data.results;
  },
  activityTypes: async () => {
    const data = await api.get<Paginated<CatalogOption> | CatalogOption[]>(
      "/catalog/activity-types/?page_size=100",
    );
    return Array.isArray(data) ? data : data.results;
  },
  eventTypes: async (lang?: string) => {
    const data = await api.get<
      Paginated<{ id: number; code: string; name: string }> | Array<{ id: number; code: string; name: string }>
    >("/catalog/event-types/?page_size=100", { lang });
    return Array.isArray(data) ? data : data.results;
  },
  /** Turlar ma'lumotnomasi (Ekologiya → Bioxilma-xillik). Sahifalash o'chirilgan. */
  species: async (params: Query = {}) => {
    const data = await api.get<Paginated<SpeciesRow> | SpeciesRow[]>(`/catalog/species/${qs(params)}`);
    return Array.isArray(data) ? data : data.results;
  },
  speciesStats: (params: Query = {}) =>
    api.get<SpeciesStats>(`/catalog/species/stats/${qs(params)}`),
};

/** O'zgarmas audit jurnali va raqamli nazorat (ЧТЗ 4.2.12). */
export const auditApi = {
  events: (limit = 8) =>
    api.get<Paginated<AuditEventItem>>(`/audit/events/?page_size=${limit}&ordering=-occurred_at`),
  eventList: (params: Query = {}) =>
    api.get<Paginated<AuditEventItem>>(`/audit/events/${qs({ ordering: "-occurred_at", ...params })}`),
  verifyIntegrity: () =>
    api.get<{ ok?: boolean; checked?: number; broken?: unknown[] } & Record<string, unknown>>(
      "/audit/events/verify-integrity/",
    ),
  oversightReport: (params: Query = {}) =>
    api.get<OversightReport>(`/audit/oversight/report/${qs(params)}`),
  oversightEvents: (params: Query = {}) =>
    api.get<Paginated<AuditEventItem>>(`/audit/oversight/events/${qs(params)}`),
  pdnAccess: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/audit/pdn-access/${qs(params)}`),
};
